import { useCallback, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { invalidateAccountVerification } from "@/modules/verification/services/accountVerification.memo";
import { invalidateCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { isProviderBalanceShort, normalizePaymentError } from "@/shared/services/payments/paymentErrors";

import { newIdempotencyKey, PaymentsPayoutService, PayoutStatus } from "../services/paymentsPayout.service";
import type { NativeQuote } from "../services/paymentsQuotes.service";
import { advancePayoutStatus } from "../utils/payoutStatusProgress";
import { classifyQuoteConflict } from "../utils/quoteConflict";
import {
  buildSendPayload,
  type SendFeeMode,
  type SendRecipient,
  type SendRoute,
  sendSuccessData,
} from "../utils/walletSend";
import { usePayoutLiveStatus } from "./usePayoutLiveStatus";
import type {
  DispersionErrorKind,
  DispersionResultPhase,
  DispersionSuccessData,
} from "./useSubmitDispersion";

export interface WalletSendInput {
  quote: NativeQuote;
  route: SendRoute;
  recipient: SendRecipient;
  concept: string;
  feeMode: SendFeeMode;
}

export interface UseWalletSendSubmitOptions {
  /** The price lapsed before or during the send: nothing moved, a fresh quote is needed. */
  onQuoteExpired: () => void;
  /** The backend claimed the quote (a rejected send, a conflict): the next attempt needs a new one. */
  onQuoteSpent: () => void;
}

/**
 * The create half of the wallet send panel, on local state: the dispersion wizard's slice is not touched. One
 * idempotency key per quote, reused by a retry of the same attempt so a lost response never pays twice.
 */
export function useWalletSendSubmit({ onQuoteExpired, onQuoteSpent }: UseWalletSendSubmitOptions) {
  const { t } = useTranslations("dispersions");
  const { user, isInternalUser } = useAuth();
  const [phase, setPhase] = useState<DispersionResultPhase>("idle");
  const [errorKind, setErrorKind] = useState<DispersionErrorKind>("generic");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<DispersionSuccessData | null>(null);
  const keyRef = useRef<string | null>(null);
  const keyQuoteRef = useRef<string | null>(null);

  const liveStatus = usePayoutLiveStatus(
    phase === "success" ? (successData?.dispersionResponse.id ?? null) : null,
  );
  // The live channel only upgrades the create response: a late reading older than it never wins.
  const payoutStatus = advancePayoutStatus(successData?.dispersionResponse.status ?? null, liveStatus);

  const submit = async (input: WalletSendInput): Promise<boolean> => {
    const organizationId = user?.organization_id ?? user?.organization?.id;
    setPhase("processing");
    setErrorMessage(null);
    setErrorKind("generic");

    try {
      if (!organizationId) throw new Error(t("error_no_organization"));
      if (Date.parse(input.quote.expires_at) <= Date.now()) {
        keyRef.current = null;
        setPhase("idle");
        onQuoteExpired();
        return false;
      }
      if (!keyRef.current || keyQuoteRef.current !== input.quote.id) keyRef.current = newIdempotencyKey();
      keyQuoteRef.current = input.quote.id;

      const { data: payout } = await PaymentsPayoutService.createPayout(
        buildSendPayload({
          organizationId,
          quote: input.quote,
          recipient: input.recipient,
          concept: input.concept,
          idempotencyKey: keyRef.current,
        }),
      );

      if (payout.status === PayoutStatus.FAILED) {
        keyRef.current = null;
        onQuoteSpent();
        throw new Error(t("error_payment_rejected"));
      }

      setSuccessData(sendSuccessData({ payout, ...input }));
      keyRef.current = null;
      setPhase("success");
      return true;
    } catch (error: unknown) {
      const normalized = normalizePaymentError(error, { quoteExpiresAt: input.quote.expires_at });

      if (normalized.kind === "quote_expired") {
        keyRef.current = null;
        setPhase("idle");
        onQuoteExpired();
        return false;
      }

      if (normalized.kind === "verification_required") {
        keyRef.current = null;
        invalidateAccountVerification();
        setErrorKind("verification_required");
        setErrorMessage(t("error_verification_required"));
        setPhase("error");
        return false;
      }

      // The rail cannot pay this amount right now (409 `provider_balance_insufficient`): refused before anything
      // was created, so the quote was not spent and there is nothing to investigate. The backend's sentence names
      // the rail to use instead. The corridor memo is dropped so the next read of the rail picker reflects it.
      const balanceShort = isProviderBalanceShort(error);
      if (balanceShort) invalidateCorridors();

      let kind: DispersionErrorKind = "generic";
      if (HttpApiService.isConflictError(error) && !balanceShort) {
        kind = await classifyQuoteConflict(input.quote.id, isInternalUser ? organizationId : undefined);
        onQuoteSpent();
      }

      const ownMessage = !HttpApiService.isAxiosError(error) && error instanceof Error ? error.message : "";
      setErrorKind(kind);
      setErrorMessage(
        kind === "generic"
          ? normalized.messages.join(" ") || ownMessage || t("error_create_failed")
          : t(kind === "quote_consumed" ? "error_quote_already_used" : "error_payout_unconfirmed"),
      );
      setPhase("error");
      return false;
    }
  };

  /** The next attempt is a different operation: the data changed. */
  const releaseKey = useCallback(() => {
    keyRef.current = null;
  }, []);

  const reset = useCallback(() => {
    keyRef.current = null;
    setPhase("idle");
    setErrorMessage(null);
    setErrorKind("generic");
    setSuccessData(null);
  }, []);

  return { phase, setPhase, errorKind, errorMessage, successData, payoutStatus, submit, releaseKey, reset };
}
